/**
 * DthTokenCrypto – Low-level cryptographic and IndexedDB primitives for the
 * validation-token manager.
 *
 * Design goals
 * ────────────
 * • Non-extractable keys: CryptoKey objects are created with `extractable: false`
 *   and stored directly in IndexedDB.  JavaScript code — including XSS payloads —
 *   can never read the raw key bytes via `crypto.subtle.exportKey()`.
 * • AES-GCM-256 authenticated encryption: the GCM authentication tag already
 *   detects ciphertext tampering; an additional HMAC-SHA-256 over the full
 *   record (id | iv | ciphertext | expiresAt) prevents cross-record swap
 *   attacks and protects the plaintext `expiresAt` field.
 * • IndexedDB persistence: tokens survive page refreshes but NOT `localStorage.clear()`,
 *   which means they are unaffected by the Matrix session-storage wipe.
 *
 * Threat model limitations
 * ────────────────────────
 * • An XSS attacker that runs code in the same origin can still call the
 *   public manager methods (get/save) to obtain plaintext tokens at runtime —
 *   the encryption cannot prevent this.  The goal is to stop offline key
 *   exfiltration and protect tokens at rest from storage inspection.
 * • Physical access to the browser profile directory can expose the IDB bytes,
 *   but they are useless without the non-exportable CryptoKey (bound to the
 *   browser's internal key storage).
 */

import { logger as baseLogger } from "matrix-js-sdk/src/logger";

const logger = baseLogger.getChild("DthTokenCrypto");

// #region IndexedDB layout
const DB_NAME = "dth-vtm";
const DB_VERSION = 1; /* DEVE ESSERE INTERO */
const KEYSTORE_STORE = "keystore";
const MASTER_KEY_ID = "master";

/**
 * Identifies which IndexedDB object store a DthTokenCrypto operation targets.
 * Pass one of these values to any CRUD method instead of a raw string.
 */
export enum DthTokenStore {
    Tokens = "tokens",
    RecoveryKeys = "recovery-keys",
}

/**
 * List of all object stores used by DthTokenCrypto.  Used for DB initialization and sanity checks.
 */
const STORES = [
    KEYSTORE_STORE,
    DthTokenStore.Tokens,
    DthTokenStore.RecoveryKeys
    // Aggiungere qui eventuali nuovi store, e ovviamente aggiornare DB_VERSION se si cambia la struttura dello store
] as const;
// #endregion IndexedDB layout

// #region Types

/**
 * Master key pair used for encryption and HMAC signing.  
 * Both keys are non-extractable CryptoKey objects, so their raw bytes are never exposed to JavaScript.
 */
interface MasterKeys {
    /** AES-GCM-256, non-extractable, usages: encrypt + decrypt */
    encKey: CryptoKey;
    /** HMAC-SHA-256, non-extractable, usages: sign + verify */
    sigKey: CryptoKey;
}

/**
 * One encrypted token record as stored in IndexedDB.
 *
 * - `id`         Composite key `"${userId}:${tokenType}"`.
 * - `iv`         12-byte random IV for AES-GCM (unique per write).
 * - `ciphertext` AES-GCM output (payload + 16-byte GCM auth tag).
 * - `signature`  HMAC-SHA-256 over `id | iv | ciphertext | expiresAt`.
 * - `expiresAt`  Unix seconds — stored in plaintext for fast expiry queries.
 */
export interface IdbEncryptedRecord {
    id: string;
    iv: Uint8Array<ArrayBuffer>;
    ciphertext: Uint8Array<ArrayBuffer>;
    signature: Uint8Array<ArrayBuffer>;
    expiresAt: number;
}
// #endregion Types

// #region DthTokenCrypto

export class DthTokenCrypto {
    private static dbPromise: Promise<IDBDatabase> | null = null;
    private static keysPromise: Promise<MasterKeys> | null = null;

    // #region IndexedDB

    /**
     * Open the IndexedDB database, ensuring all required object stores are present.
     * @returns A promise that resolves to an open IDBDatabase instance.
     */
    private static openDB(): Promise<IDBDatabase> {
        if (this.dbPromise) return this.dbPromise;
        this.dbPromise = (async (): Promise<IDBDatabase> => {
            let db = await DthTokenCrypto.openIDBRaw(DB_VERSION);
            // onupgradeneeded fires only on version changes, so a store deleted
            // after first run would never be recreated. If any required store is
            // missing, delete the whole DB and recreate it at the same DB_VERSION
            // (no schema change — this is recovery from user/tool-level corruption).
            // Any data in the missing stores is already gone; the master keys would
            // be useless without the ciphertext records anyway.
            if (STORES.some((s) => !db.objectStoreNames.contains(s))) {
                logger.warn("One or more IDB stores are missing — dropping and recreating the database");
                db.close();
                await DthTokenCrypto.deleteIDB();
                db = await DthTokenCrypto.openIDBRaw(DB_VERSION);
            }
            return db;
        })().catch((err) => {
            this.dbPromise = null; // allow retry on next call
            throw err;
        });

        return this.dbPromise;
    }

    /**
     * Open the IndexedDB database at the specified version, creating object stores if needed.
     * This is a low-level helper that does not handle the "missing store" case — use `openDB()` instead.
     */
    private static openIDBRaw(version: number): Promise<IDBDatabase> {
        return new Promise<IDBDatabase>((resolve, reject) => {
            const req = indexedDB.open(DB_NAME, version);

            req.onupgradeneeded = (evt) => {
                const db = (evt.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(KEYSTORE_STORE)) {
                    db.createObjectStore(KEYSTORE_STORE, { keyPath: "id" });
                }
                if (!db.objectStoreNames.contains(DthTokenStore.Tokens)) {
                    const store = db.createObjectStore(DthTokenStore.Tokens, { keyPath: "id" });
                    // Index on expiresAt enables efficient bulk-delete of expired records.
                    store.createIndex("expiresAt", "expiresAt", { unique: false });
                }
                if (!db.objectStoreNames.contains(DthTokenStore.RecoveryKeys)) {
                    const store = db.createObjectStore(DthTokenStore.RecoveryKeys, { keyPath: "id" });
                    store.createIndex("expiresAt", "expiresAt", { unique: false });
                }
                // Aggiungere qui eventuali nuovi store, con relative chiavi e indici
            };

            req.onsuccess = (evt) => resolve((evt.target as IDBOpenDBRequest).result);
            req.onerror = (evt) => {
                const err = (evt.target as IDBOpenDBRequest).error;
                logger.error("Failed to open IDB", err);
                reject(err);
            };
            req.onblocked = () => {
                logger.warn("IDB open blocked — close other tabs holding an older DB version");
            };
        });
    }

    /**
     * Delete the entire IndexedDB database.  Use with caution — this is irreversible and will cause all stored tokens to be lost.
     * @returns A promise that resolves when the deletion is complete.
     */
    private static deleteIDB(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const req = indexedDB.deleteDatabase(DB_NAME);
            req.onsuccess = () => resolve();
            req.onerror = (evt) => reject((evt.target as IDBOpenDBRequest).error);
            req.onblocked = () => {
                logger.warn("IDB delete blocked — close other tabs holding a connection");
            };
        });
    }

    // #endregion IndexedDB

    // #region Key management

    /**
     * Generate a fresh master key pair (AES-GCM for encryption + HMAC-SHA-256 for integrity).
     */
    private static async generateMasterKeys(): Promise<MasterKeys> {
        const [encKey, sigKey] = await Promise.all([
            crypto.subtle.generateKey(
                { name: "AES-GCM", length: 256 },
                false, // non-extractable: raw key bytes are never exposed to JS
                ["encrypt", "decrypt"],
            ) as Promise<CryptoKey>,
            crypto.subtle.generateKey(
                { name: "HMAC", hash: "SHA-256" },
                false,
                ["sign", "verify"],
            ) as Promise<CryptoKey>,
        ]);
        return { encKey, sigKey };
    }

    /**
     * Load the master key pair from IDB, or generate and persist a new one.
     * The result is cached in memory for the lifetime of the page.
     */
    public static getOrCreateMasterKeys(): Promise<MasterKeys> {
        if (this.keysPromise) return this.keysPromise;
        this.keysPromise = (async (): Promise<MasterKeys> => {
            const db = await this.openDB();
            const existing = await idbGet<{ id: string; encKey: CryptoKey; sigKey: CryptoKey }>(
                db, KEYSTORE_STORE, MASTER_KEY_ID,
            );
            if (existing) return { encKey: existing.encKey, sigKey: existing.sigKey };

            const keys = await this.generateMasterKeys();
            await idbPut(db, KEYSTORE_STORE, {
                id: MASTER_KEY_ID,
                encKey: keys.encKey,
                sigKey: keys.sigKey,
            });
            return keys;
        })().catch((err) => {
            this.keysPromise = null;
            throw err;
        });

        return this.keysPromise;
    }

    // #endregion Key management

    // #region Encryption / Decryption

    /**
     * Encrypt `plaintext` and produce an {@link IdbEncryptedRecord} ready for IDB storage.
     *
     * @param plaintext  UTF-8 string to protect.
     * @param id         Record identifier (used in HMAC to prevent record-swap attacks).
     * @param expiresAt  Token expiry as Unix seconds (included in HMAC to prevent tampering).
     */
    public static async encrypt(plaintext: string, id: string, expiresAt: number): Promise<IdbEncryptedRecord> {
        const { encKey, sigKey } = await this.getOrCreateMasterKeys();
        const iv = crypto.getRandomValues(new Uint8Array(12));
        const encoded = new TextEncoder().encode(plaintext);
        const ciphertextBuf = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, encKey, encoded);
        const ciphertext = new Uint8Array(ciphertextBuf);
        const sigBuf = await crypto.subtle.sign(
            "HMAC",
            sigKey,
            buildSignatureInput(id, iv, ciphertext, expiresAt),
        );
        return { id, iv, ciphertext, signature: new Uint8Array(sigBuf), expiresAt };
    }

    /**
     * Verify the HMAC and decrypt a previously encrypted record.
     *
     * Returns `null` when:
     * - HMAC verification fails (record was tampered with).
     * - AES-GCM decryption fails (wrong key or corrupted ciphertext).
     */
    public static async decrypt(record: IdbEncryptedRecord): Promise<string | null> {
        const { encKey, sigKey } = await this.getOrCreateMasterKeys();

        const valid = await crypto.subtle.verify(
            "HMAC",
            sigKey,
            record.signature,
            buildSignatureInput(record.id, record.iv, record.ciphertext, record.expiresAt),
        );
        if (!valid) {
            logger.warn("HMAC verification failed — record may have been tampered with:", record.id);
            return null;
        }

        try {
            const buf = await crypto.subtle.decrypt(
                { name: "AES-GCM", iv: record.iv },
                encKey,
                record.ciphertext,
            );
            return new TextDecoder().decode(buf);
        } catch (e) {
            logger.warn("AES-GCM decryption failed for record:", record.id, e);
            return null;
        }
    }

    // #endregion Encryption / Decryption

    // #region IDB token CRUD

    /**
     * Save a record to the specified store.
     * @param record The record to save.
     * @param store The target object store.
     */
    public static async saveRecord(record: IdbEncryptedRecord, store: DthTokenStore): Promise<void> {
        const db = await this.openDB();
        await idbPut(db, store, record);
    }

    /**
     * Load a record by ID from the specified store.
     * @param id The record ID to load.
     * @param store The target object store.
     */
    public static async loadRecord(id: string, store: DthTokenStore): Promise<IdbEncryptedRecord | null> {
        const db = await this.openDB();
        return idbGet<IdbEncryptedRecord>(db, store, id);
    }

    /**
     * Delete a record by ID from the specified store.
     * @param id The record ID to delete.
     * @param store The target object store.
     */
    public static async deleteRecord(id: string, store: DthTokenStore): Promise<void> {
        const db = await this.openDB();
        await idbDelete(db, store, id);
    }

    /**
     * Clear all records from the specified store.  
     * Use with caution — this is irreversible and will cause all stored tokens in that store to be lost.
     * @param store The target object store.
     */
    public static async clearAll(store: DthTokenStore): Promise<void> {
        const db = await this.openDB();
        await idbClear(db, store);
    }

    /**
     * Get a list of all record IDs in the specified store.
     * @param store The target object store.
     * @returns A promise that resolves to an array of record IDs (strings) present in the store.
     */
    public static async getAllIds(store: DthTokenStore): Promise<string[]> {
        const db = await this.openDB();
        return new Promise<string[]>((resolve, reject) => {
            const tx = db.transaction(store, "readonly");
            const req = tx.objectStore(store).getAllKeys();
            req.onsuccess = (evt) => resolve((evt.target as IDBRequest<IDBValidKey[]>).result as string[]);
            req.onerror = (evt) => reject((evt.target as IDBRequest).error);
        });
    }

    /**
     * Delete all records whose `expiresAt` is ≤ now.
     * Uses the IDB index — no decryption required.
     *
     * @param store The target object store.
     * @returns Number of deleted records.
     */
    public static async deleteExpired(store: DthTokenStore): Promise<number> {
        const db = await this.openDB();
        const nowSec = Math.floor(Date.now() / 1000);
        return new Promise<number>((resolve, reject) => {
            const tx = db.transaction(store, "readwrite");
            const index = tx.objectStore(store).index("expiresAt");
            // Range: (−∞, nowSec] — all records whose expiry has already passed.
            const range = IDBKeyRange.upperBound(nowSec, false);
            let count = 0;
            const req = index.openCursor(range);
            req.onsuccess = (evt) => {
                const cursor = (evt.target as IDBRequest<IDBCursorWithValue | null>).result;
                if (cursor) {
                    cursor.delete();
                    count++;
                    cursor.continue();
                }
            };
            tx.oncomplete = () => resolve(count);
            tx.onerror = (evt) => reject((evt.target as IDBTransaction).error);
        });
    }

    /**
     * Wipe ALL data — both the master keys and all token records.
     * On the next call that needs keys a fresh key pair will be generated,
     * rendering all previously stored ciphertexts permanently unrecoverable.
     */
    public static async nuke(): Promise<void> {
        const db = await this.openDB();
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORES, "readwrite");
            STORES.forEach((store) => {
                try {
                    tx.objectStore(store).clear();
                } catch (e) {
                    logger.warn(`Failed to clear store ${store}:`, e);
                }
            });
            tx.oncomplete = () => resolve();
            tx.onerror = (evt) => reject((evt.target as IDBTransaction).error);
        });
        this.keysPromise = null; // drop in-memory key reference
    }

    // #endregion IDB token CRUD
}

// #endregion

// #region HMAC input builder

/**
 * Canonical byte layout fed to HMAC sign/verify:
 *   UTF-8(id) | iv (12 B) | ciphertext | expiresAt (8 B, big-endian uint64)
 *
 * Binding `id` prevents an attacker from copying a valid record under a
 * different key.  Binding `expiresAt` prevents extending a token's lifetime
 * by modifying the plaintext field in IDB.
 */
function buildSignatureInput(id: string, iv: Uint8Array<ArrayBuffer>, ciphertext: Uint8Array<ArrayBuffer>, expiresAt: number): Uint8Array<ArrayBuffer> {
    const idBytes = new TextEncoder().encode(id);
    const expBytes = new Uint8Array(8);
    new DataView(expBytes.buffer).setBigUint64(0, BigInt(Math.floor(expiresAt)), false);

    const out = new Uint8Array(idBytes.length + iv.length + ciphertext.length + expBytes.length);
    let off = 0;
    out.set(idBytes, off); off += idBytes.length;
    out.set(iv, off); off += iv.length;
    out.set(ciphertext, off); off += ciphertext.length;
    out.set(expBytes, off);
    return out;
}

// #endregion HMAC input builder

// #region IDB utility wrappers

/**
 * Promisified wrapper for IDB get operation.
 * @param db The target IDB database.
 * @param store The target object store.
 * @param key The key of the record to retrieve.
 * @returns A promise that resolves to the record value or null if not found.
 */
function idbGet<T>(db: IDBDatabase, store: string, key: string): Promise<T | null> {
    return new Promise<T | null>((resolve, reject) => {
        const tx = db.transaction(store, "readonly");
        const req = tx.objectStore(store).get(key);
        req.onsuccess = (evt) => resolve(((evt.target as IDBRequest).result as T) ?? null);
        req.onerror = (evt) => reject((evt.target as IDBRequest).error);
    });
}

/**
 * Promisified wrapper for IDB put operation.
 * @param db The target IDB database.
 * @param store The target object store.
 * @param value The value to store (must include the keyPath field).
 * @returns A promise that resolves when the operation is complete.
 */
function idbPut(db: IDBDatabase, store: string, value: object): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const tx = db.transaction(store, "readwrite");
        tx.objectStore(store).put(value);
        tx.oncomplete = () => resolve();
        tx.onerror = (evt) => reject((evt.target as IDBTransaction).error);
    });
}

/**
 * Promisified wrapper for IDB delete operation.
 * @param db The target IDB database.
 * @param store The target object store.
 * @param key The key of the record to delete.
 * @returns A promise that resolves when the operation is complete.
 */
function idbDelete(db: IDBDatabase, store: string, key: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const tx = db.transaction(store, "readwrite");
        tx.objectStore(store).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = (evt) => reject((evt.target as IDBTransaction).error);
    });
}

/**
 * Promisified wrapper for IDB clear operation.
 * @param db The target IDB database.
 * @param store The target object store.
 * @returns A promise that resolves when the operation is complete.
 */
function idbClear(db: IDBDatabase, store: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const tx = db.transaction(store, "readwrite");
        tx.objectStore(store).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = (evt) => reject((evt.target as IDBTransaction).error);
    });
}
// #endregion IDB utility wrappers
